/**
 * PMS contact-type bindings for one matter — read, and the explicit unbind.
 *
 * Provider-agnostic like every other helper here: the backend route is already parameterised by
 * provider, so the transport needs one implementation rather than one per PMS.
 *
 * Throws on failure (`DirectApiError`) rather than returning a result envelope — the `client.ts`
 * convention for `directApiClient`-backed helpers.
 */

'use client';

import { directApiClient } from '@/lib/api/direct-client';
import type { PmsContactBinding, PmsProvider } from './types';

function pmsRoute(provider: PmsProvider, path: string): string {
  return `/customer-integrations/pms/${provider}/${path}`;
}

/**
 * Every recorded contact-type selection on one matter, with its bind outcome.
 *
 * A contact with no entry has had no type chosen — which under this design means it is not bound at
 * all, since there is no default and no fallback.
 */
export async function getPmsMatterContactBindings(
  provider: PmsProvider,
  matterId: number
): Promise<PmsContactBinding[]> {
  const data = await directApiClient.get<{
    bindings: PmsContactBinding[];
  }>(pmsRoute(provider, `matters/${matterId}/contact-bindings`));

  return data.bindings;
}

/**
 * Remove a contact's link from the matter in the PMS and clear its stored selection.
 *
 * This is always an explicit user act. Vale never unbinds on the user's behalf — it does not
 * reconcile removals, and changing a contact's type leaves the old link in place — so nothing but a
 * deliberate action may call this.
 */
export async function unbindPmsMatterContact(
  provider: PmsProvider,
  matterId: number,
  contactId: number
): Promise<{ unlinkedInPms: boolean }> {
  return directApiClient.delete<{ unlinkedInPms: boolean }>(
    pmsRoute(provider, `matters/${matterId}/contacts/${contactId}/binding`)
  );
}
